import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  AlertCircle, QrCode, Copy, Loader2, CheckCircle,
  ExternalLink, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';
import ChannelBehaviorEditor from '@/components/chatbot/ChannelBehaviorEditor';
import logger from '@/utils/logger';

/**
 * Persistent panel that surfaces the Meta App Dashboard webhook credentials
 * (Webhook URL + Verify Token) needed to actually receive Facebook Messenger
 * events. Rendered inline whenever a Facebook page is connected so admins
 * don't have to click "Diagnostics" first — the previous UX led to users
 * completing OAuth but never registering the App-level webhook, so no
 * messages arrived.
 */
const FacebookAppSetupPanel = ({ chatbotId }) => {
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [receiving, setReceiving] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/chatbots/${chatbotId}/channels/facebook/webhook-status`);
        if (cancelled) return;
        setSetup(r.data?.app_webhook_setup || null);
        setReceiving(!!r.data?.last_webhook_received_at);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Could not load webhook setup');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [chatbotId]);

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value || '');
      toast.success(`${label} copied`);
    } catch (_) {
      toast.error('Copy failed — please select and copy manually');
    }
  };

  if (loading) {
    return (
      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600 flex items-center gap-2" data-testid="fb-app-setup-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading webhook setup…
      </div>
    );
  }

  if (error || !setup) {
    return (
      <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700" data-testid="fb-app-setup-error">
        {error || 'Webhook setup unavailable'}
      </div>
    );
  }

  const webhookUrl = setup.webhook_url || '';
  const verifyToken = setup.verify_token || '';

  return (
    <div
      className="p-4 rounded-lg border border-blue-200 bg-blue-50/60 space-y-3"
      data-testid="fb-app-setup-panel"
    >
      <div className="flex items-start gap-2">
        <ShieldCheck className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-blue-900">Meta App Dashboard — Webhook setup</p>
          <p className="text-xs text-blue-800 mt-1">
            Paste these values into your Meta App under <span className="font-medium">Products → Messenger → Webhooks → Page</span>,
            then subscribe to the <span className="font-medium">messages</span> and <span className="font-medium">messaging_postbacks</span> fields.
            Without this step Meta will not deliver messages to Harmonix.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <div>
          <Label className="text-xs text-slate-700">Callback URL</Label>
          <div className="flex items-center gap-2 mt-1">
            <Input
              value={webhookUrl}
              readOnly
              className="text-xs font-mono bg-white"
              data-testid="fb-webhook-url-input"
              onFocus={(e) => e.target.select()}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => copy(webhookUrl, 'Webhook URL')}
              data-testid="fb-copy-webhook-url"
            >
              <Copy className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-700">Verify Token</Label>
          <div className="flex items-center gap-2 mt-1">
            <Input
              value={verifyToken}
              readOnly
              className="text-xs font-mono bg-white"
              data-testid="fb-verify-token-input"
              onFocus={(e) => e.target.select()}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => copy(verifyToken, 'Verify Token')}
              data-testid="fb-copy-verify-token"
            >
              <Copy className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        <div className="text-xs text-slate-700">
          <span className="font-medium">Subscription fields:</span>{' '}
          <code className="px-1 py-0.5 rounded bg-white border border-slate-200">messages</code>{', '}
          <code className="px-1 py-0.5 rounded bg-white border border-slate-200">messaging_postbacks</code>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <a
          href="https://developers.facebook.com/apps/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900 underline underline-offset-2"
          data-testid="fb-open-meta-dashboard"
        >
          Open Meta App Dashboard <ExternalLink className="w-3 h-3" />
        </a>
        {receiving === true && (
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]" data-testid="fb-webhook-receiving-badge">
            <CheckCircle className="w-3 h-3 mr-1" /> Receiving events
          </Badge>
        )}
        {receiving === false && (
          <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]" data-testid="fb-webhook-not-receiving-badge">
            <AlertCircle className="w-3 h-3 mr-1" /> No events received yet
          </Badge>
        )}
      </div>
    </div>
  );
};

export const FacebookChannel = ({
  chatbotId,
  channels,
  loadChannels,
  disconnectChannel,
  openQR,
}) => {
  const connected = !!channels.facebook?.connected;
  return (
    <div className="p-4 rounded-xl border border-slate-200 bg-gradient-to-r from-white to-blue-50/30">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#1877F2] flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          </div>
          <div>
            <h4 className="font-semibold text-slate-900">Facebook Pages</h4>
            <p className="text-sm text-slate-500">Auto-reply to Messenger and comments</p>
          </div>
        </div>
        {connected && (
          <Badge className="bg-blue-100 text-blue-700">
            <CheckCircle className="w-3 h-3 mr-1" /> Connected
          </Badge>
        )}
      </div>

      {!connected ? (
        <div className="space-y-3">
          <div className="rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/50 p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <QrCode className="w-5 h-5 text-blue-700" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-blue-900">Easiest: connect from your phone</p>
                <p className="text-xs text-blue-700 mt-0.5">Scan a QR code, approve in the Facebook app — done.</p>
              </div>
              <Button
                size="sm"
                onClick={() => openQR('facebook', 'Facebook')}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="facebook-qr-connect-btn"
              >
                <QrCode className="w-4 h-4 mr-1.5" /> Show QR
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <div className="flex-1 h-px bg-slate-200"></div>
            <span>or connect from this browser</span>
            <div className="flex-1 h-px bg-slate-200"></div>
          </div>
          <p className="text-sm text-slate-600">Sign in with your Facebook account and select a Page.</p>
          <Button
            onClick={async () => {
              try {
                const response = await api.get(`/chatbots/${chatbotId}/channels/facebook/oauth`);
                window.open(response.data.oauth_url, 'facebook-auth', 'width=600,height=700,popup=1');

                const handleMessage = (event) => {
                  if (event.data.type === 'FACEBOOK_AUTH_SUCCESS') {
                    toast.success(`Connected to ${event.data.pageName}`);
                    loadChannels();
                    window.removeEventListener('message', handleMessage);
                  } else if (event.data.type === 'FACEBOOK_AUTH_ERROR') {
                    toast.error(event.data.error || 'Facebook connection failed');
                    window.removeEventListener('message', handleMessage);
                  }
                };
                window.addEventListener('message', handleMessage);
              } catch (error) {
                toast.error(error.response?.data?.detail || 'Failed to start Facebook connection');
              }
            }}
            className="w-full bg-[#1877F2] hover:bg-[#166FE5] text-white"
          >
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            Sign in with Facebook
          </Button>
          <p className="text-xs text-slate-500 text-center">No API keys needed - just sign in and select your Page</p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">Connected to {channels.facebook?.page_name || 'Facebook Page'}</p>
              <p className="text-xs text-blue-700">
                {channels.facebook?.webhook_subscribed === false
                  ? <span className="text-amber-700">⚠ Webhook subscription missing — messages won&apos;t arrive. Click &quot;Fix webhook&quot; below.</span>
                  : 'Page subscribed to app webhooks'}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => disconnectChannel('facebook')} className="text-red-600 border-red-200 hover:bg-red-50">
              Disconnect
            </Button>
          </div>

          <FacebookAppSetupPanel chatbotId={chatbotId} />

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const r = await api.post(`/chatbots/${chatbotId}/channels/facebook/resubscribe-webhook`);
                  if (r.data?.ok) toast.success('Page re-subscribed — messages will now arrive');
                  else toast.error('Re-subscribe failed');
                  loadChannels?.();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Re-subscribe failed');
                }
              }}
              data-testid="fb-resubscribe-btn"
            >
              Fix webhook (re-subscribe page)
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                try {
                  const r = await api.get(`/chatbots/${chatbotId}/channels/facebook/webhook-status`);
                  const d = r.data;
                  const live = d.live || {};
                  const fbSetup = d.app_webhook_setup || {};
                  const fields = Array.isArray(live.subscribed_fields)
                    ? live.subscribed_fields.join(', ')
                    : '—';
                  const lines = [
                    `Stored subscribed: ${d.webhook_subscribed ? 'yes' : 'NO'}`,
                    `Last webhook received: ${d.last_webhook_received_at || 'never'}`,
                    `Live token valid: ${live.token_valid === null ? 'unknown' : (live.token_valid ? 'yes' : 'NO — re-connect FB')}`,
                    `Live messages-field subscribed: ${live.messages_field_subscribed ? 'yes' : 'NO'}`,
                    `Live subscribed fields: ${fields}`,
                    d.webhook_subscribe_error ? `Stored error: ${d.webhook_subscribe_error}` : '',
                    live.token_error ? `Live token error: ${live.token_error}` : '',
                    live.subscribed_error ? `Live subscribed error: ${live.subscribed_error}` : '',
                    '',
                    'Meta App Dashboard must also have:',
                    `  Webhook URL: ${fbSetup.webhook_url || '(PUBLIC_BACKEND_URL not configured)'}`,
                    `  Verify Token: ${fbSetup.verify_token || '(missing)'}`,
                    '  Fields: messages, messaging_postbacks',
                  ].filter(Boolean).join('\n');
                  toast.message('Facebook webhook diagnostics', {
                    description: lines,
                    duration: 30000,
                  });
                  logger.log('[FB Webhook Diagnostics]', d);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Could not load diagnostics');
                }
              }}
              data-testid="fb-webhook-diagnostics-btn"
            >
              Diagnostics
            </Button>
          </div>
          <ChannelBehaviorEditor chatbotId={chatbotId} platform="facebook" label="Facebook" />
        </div>
      )}
    </div>
  );
};

export default FacebookChannel;
